import { Tooltip } from 'antd';
import { FilterItem, FilterRow } from 'components/app/FilterBar/components';
import { LabelsFilter, SearchFilter } from 'components/app/FilterBar/filters';
import Paginator from 'components/app/Paginator';
import DateTime from 'components/ui/DateTime';
import DopeIcon from 'components/ui/DopeIcon';
import IconButton from 'components/ui/IconButton';
import Label from 'components/ui/Label';
import Table from 'components/ui/Table';
import QueryResult from 'components/util/QueryResult';
import { DocumentNode } from 'graphql';
import gql from 'graphql-tag';
import React from 'react';
import { OperationVariables, Query } from 'react-apollo';
import { Route } from 'react-router';
import { Link } from 'react-router-dom';
import { AutomationEventDefinition, PageInfo } from 'typings';


const UNIQUE_EVENT_LABELS_QUERY = gql`
  query uniqueEventDefinitionLabels {
    uniqueEventDefinitionLabels
  }
`;

const SORT_BY_MAP = {
  name: 'name',
  updated_at: 'updated_at'
};

const SORT_ORDER_MAP = {
  ascend: 'ASC',
  descend: 'DESC'
};

interface Props {
  query: DocumentNode;
  variables?: OperationVariables;
  getEventsRootFromQueryResult: (
    data: any
  ) => {
    nodes: AutomationEventDefinition[];
    pageInfo: PageInfo;
  };
}

const FILTERS = [
  {
    name: 'labels',
    label: 'Label',
    qsKey: 'labels',
    many: true
  },
  {
    name: 'cloudVendor',
    label: 'Cloud Vendor',
    qsKey: 'cloudVendor',
    many: true
  }
];

function EventsTable(props: Props) {
  const { query, variables = {}, getEventsRootFromQueryResult } = props;

  return (
    <Paginator pageSize={20} filters={FILTERS}>
      {pageRenderProps => {
        const {
          pageNumber,
          pageSize,
          sortBy,
          sortDirection,
          updateSearchParams,
          updateSearch,
          search,
          filterValues,
          pushFilters,
          replaceFilters
        } = pageRenderProps;

        const queryVariables = {
          search,
          labels: filterValues.labels,
          ...variables,
          sortBy: sortBy || 'name',
          sortDirection: sortDirection || 'ASC',
          pageNumber,
          pageSize
        };

        const allColumns = [
          {
            title: '',
            key: 'send',
            dataIndex: 'send',
            render: (text, item) => {
              return (
                <Route
                  render={({ history }) => {
                    return (
                      <IconButton
                        title="Edit &amp; Send Event"
                        size="small"
                        onClick={() =>
                          history.push({
                            pathname: `/dev/events/${item.id}/send`
                          })
                        }
                      >
                        <DopeIcon name="CHEVRON_RIGHT" />
                      </IconButton>
                    );
                  }}
                />
              );
            }
          },
          {
            title: 'Name',
            key: 'name',
            dataIndex: 'name',
            sorter: true,
            render: (text, item) => {
              return (
                <Link to={`./events/${item.id}`} title="View Event Details">
                  {text}
                </Link>
              );
            }
          },
          {
            title: 'Vendor',
            key: 'vendor',
            dataIndex: 'vendor',
            render: (text, item) => item?.context?.vendor
          },
          {
            title: 'Product',
            key: 'product',
            dataIndex: 'product',
            render: (text, item) => item?.context?.vendorProduct
          },
          {
            title: 'Labels',
            key: 'labels',
            dataIndex: 'labels',
            render: labels => {
              if (!labels) return null;

              return (
                <div>
                  {labels &&
                    labels.map((l, idx) => (
                      <Label
                        key={`${l}_${idx}`}
                        onClick={() => {
                          replaceFilters({
                            search: '',
                            s: '',
                            labels: l
                          });
                        }}
                        label={l}
                      />
                    ))}
                </div>
              );
            }
          },
          {
            title: 'Description',
            key: 'description',
            dataIndex: 'description',
            render: (text, item) => {
              return <Tooltip title={item.overview}>{text}</Tooltip>;
            }
          },
          {
            title: 'Updated',
            key: 'updatedAt',
            sorter: true,
            dataIndex: 'updatedAt',
            className: 'no-wrap',
            render: updatedAt => {
              if (!updatedAt) return '--';

              return <DateTime dateTime={updatedAt} />;
            }
          }
        ];

        const columns = allColumns;

        return (
          <>
            <FilterRow>
              <FilterItem label="Search">
                <SearchFilter search={search} onSearchChange={updateSearch} />
              </FilterItem>
              <FilterItem label="Labels">
                <LabelsFilter
                  value={(Array.isArray(filterValues.labels) && filterValues.labels) || []}
                  onChange={value => {
                    pushFilters('labels', value);
                  }}
                  query={UNIQUE_EVENT_LABELS_QUERY}
                  getLabelsRootFromQueryResult={data => data && data.uniqueEventDefinitionLabels}
                />
              </FilterItem>
            </FilterRow>
            <Query query={query} variables={queryVariables}>
              {({ loading, data, error }) => (
                <QueryResult loading={loading} data={data} error={error}>
                  {() => {
                    const { nodes: functions, pageInfo } = getEventsRootFromQueryResult(data);

                    return (
                      <Table
                        antTableProps={{
                          loading,
                          dataSource: functions,
                          rowKey: 'id',
                          columns,
                          locale: {
                            emptyText: 'No matching Events'
                          },
                          onChange: (pagination, filters, sorter, a) => {
                            updateSearchParams({
                              sortBy: SORT_BY_MAP[sorter.columnKey] || 'name',
                              sortDirection: SORT_ORDER_MAP[sorter.order] || SORT_ORDER_MAP.descend,
                              page: pagination.current || 1
                            });
                          },
                          pagination: {
                            pageSize,
                            current: pageInfo && pageInfo.current,
                            total: pageInfo && pageInfo.total,
                            onChange: pageNumber => {
                              updateSearchParams({
                                page: pageNumber
                              });
                            }
                          }
                        }}
                      />
                    );
                  }}
                </QueryResult>
              )}
            </Query>
          </>
        );
      }}
    </Paginator>
  );
}

export default EventsTable;
